/**
 * Geography for the empowerment programme.
 *
 * The applicant's LGA dropdown always lists every Niger State LGA, so the
 * form looks the same regardless of which are actually open — which LGAs
 * are accepted is controlled from admin settings
 * (`EmpowermentSettings.allowedLgas`), not hard-coded or stated on the page
 * (R1/R2/R4, R5). An applicant outside the open set finds out at the ward
 * step, not before starting.
 *
 * Decision D5 (permanently Lapai-only) is superseded by R5.
 */

export const STATE = "Niger" as const;

/** Kept as the default/primary LGA — the one row `WARDS_BY_LGA` ships populated. */
export const LGA = "Lapai" as const;

export const LG_NAMES = [
  "Agaie",
  "Agwara",
  "Bida",
  "Borgu",
  "Bosso",
  "Chanchaga",
  "Edati",
  "Gbako",
  "Gurara",
  "Katcha",
  "Kontagora",
  "Lapai",
  "Lavun",
  "Magama",
  "Mariga",
  "Mashegu",
  "Mokwa",
  "Munya",
  "Paikoro",
  "Rafi",
  "Rijau",
  "Shiroro",
  "Suleja",
  "Tafa",
  "Wushishi",
] as const;

export type NigerLga = (typeof LG_NAMES)[number];

/** The ten wards of Lapai LGA. */
export const LAPAI_WARDS = [
  "Gabas/Kudu",
  "Arewa/Yamma",
  "Duma/Zago",
  "Takuti/Shaku",
  "Birnin Maza/Tashibo",
  "Gulu/Anguwa",
  "Evuti/Kpada",
  "Ebbo/Gbacinku",
  "Muye/Egba",
  "Gupa/Abugi",
] as const;

export type LapaiWard = (typeof LAPAI_WARDS)[number];

/**
 * Ward names by LGA. Only Lapai is populated — the other 24 are present but
 * empty rather than absent, so `wardsForLga` can return `[]` for a known LGA
 * (fall back to free text) instead of `undefined` (a bug). Real names for
 * the other 24 must come from INEC's delimitation list or the NPC, never
 * invented: an invented ward name is indistinguishable from a real one in
 * review and would land on applicant records and the public results page as
 * fact.
 */
export const WARDS_BY_LGA: Record<NigerLga, readonly string[]> = {
  Agaie: [],
  Agwara: [],
  Bida: [],
  Borgu: [],
  Bosso: [],
  Chanchaga: [],
  Edati: [],
  Gbako: [],
  Gurara: [],
  Katcha: [],
  Kontagora: [],
  Lapai: LAPAI_WARDS,
  Lavun: [],
  Magama: [],
  Mariga: [],
  Mashegu: [],
  Mokwa: [],
  Munya: [],
  Paikoro: [],
  Rafi: [],
  Rijau: [],
  Shiroro: [],
  Suleja: [],
  Tafa: [],
  Wushishi: [],
};

export function isNigerLga(value: string | null | undefined): value is NigerLga {
  if (!value) return false;
  return (LG_NAMES as readonly string[]).includes(value);
}

/**
 * The known wards for an LGA, or `[]` if none are loaded yet — the caller's
 * ward dropdown is simply empty (bar the placeholder) in that case, rather
 * than falling back to free text. Not a gate: with no ward restriction (R5),
 * this is a UI convenience (the applicant's dropdown, the reviewer's
 * filter), not something an application can be rejected against.
 */
export function wardsForLga(lga: string | null | undefined): readonly string[] {
  if (!isNigerLga(lga)) return [];
  return WARDS_BY_LGA[lga];
}
